import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { AxiosError } from 'axios';

import { cloneDeep, isEqual } from 'lodash';

import { ProtectorType } from 'core/constants';

import API from 'utils/api';
import RootStore from 'stores/Root';
import DataMixin from 'utils/stores/DataMixin';
import { apiErrorsWithKeys } from 'utils/api/errors';

import { formToModel, mintKey, modelToForm, WireForm } from './transform';
import { AUDITS_BUILDER_MODE, FormBuilderMode } from './mode';
import { readableAuditSaveErrors } from './serverErrors';
import { rulesTriggeredBy } from './rules';
import { Blocker, publishBlockers, summarisePublishBlockers } from './validation';
import {
  ResponseSet,
  responseSetId,
  responseSetIdentity,
  responseSetLabel,
  responseSetLabels,
  SEEDED_RESPONSE_SETS,
  yesNoNaOptions,
} from './responseSets';
import {
  AuditAction,
  AuditFormModel,
  AuditOption,
  AuditQuestion,
  AuditSection,
  CHOICE_TYPES,
  SECTION_NEXT_DEFAULT,
  SECTION_NEXT_SUBMIT,
  sectionTitleId,
  SINGLE_ANSWER_CHOICE_TYPES,
} from './types';

export const NEW_TEMPLATE_ID = 'new';

const emptyModel = (): AuditFormModel => ({
  sections: [{ key: mintKey(), title: 'Section 1', description: '', questions: [], actions: [] }],
});

export default class AuditFormBuilder extends DataMixin {
  @observable error?: AxiosError;

  @observable templateId: string = NEW_TEMPLATE_ID;

  @observable name = '';

  @observable model: AuditFormModel = emptyModel();

  @observable lastPublished?: string;

  @observable isPublishing = false;

  @observable saveErrors: string[] = [];

  @observable.ref mode: FormBuilderMode = AUDITS_BUILDER_MODE;

  @observable private savedSnapshot?: { name: string; form: WireForm };

  /** Images uploaded and then replaced in this session — deleted server-side once the save succeeds. */
  private orphanedImageUuids: string[] = [];

  private latestLoad = 0;

  constructor(rootStore: RootStore, api: typeof API) {
    super(rootStore, api);
    makeObservable(this);
  }

  @computed
  get isNew(): boolean {
    return this.templateId === NEW_TEMPLATE_ID;
  }

  @computed
  get hasUnpublishedChanges(): boolean {
    if (!this.savedSnapshot) return true;

    return this.savedSnapshot.name !== this.name || !isEqual(this.savedSnapshot.form, this.toForm());
  }

  /** One entry per offender; Publish reads the length, its tooltip the summary. */
  @computed
  get publishBlockers(): Blocker[] {
    return publishBlockers(this.model, this.name, this.mode);
  }

  @computed
  get publishBlockerSummary(): string[] {
    return summarisePublishBlockers(this.publishBlockers);
  }

  @computed
  get holdsUnsavedWork(): boolean {
    return this.isNew && (this.name.trim() !== '' || this.model.sections.some((s) => s.questions.length > 0));
  }

  @action.bound
  reset(): void {
    super.reset();
    this.error = undefined;
    this.templateId = NEW_TEMPLATE_ID;
    this.name = '';
    this.model = emptyModel();
    this.lastPublished = undefined;
    this.savedSnapshot = undefined;
    this.isPublishing = false;
    this.saveErrors = [];
    this.orphanedImageUuids = [];
  }

  private toForm(): WireForm {
    return modelToForm(this.model, { conditionalLogic: this.mode.conditionalLogic });
  }

  @action.bound
  async load(templateId: string, mode: FormBuilderMode): Promise<void> {
    this.reset();
    this.templateId = templateId;
    this.mode = mode;
    const load = ++this.latestLoad;

    if (templateId === NEW_TEMPLATE_ID) {
      runInAction(() => {
        this.isLoaded = true;
      });
      return;
    }

    try {
      const { data } = await this.api.entities.loadEntityForm(this.mode.entityType, templateId)();
      if (load !== this.latestLoad) return;
      const form = data.form as unknown as WireForm;

      runInAction(() => {
        this.name = data.name;
        this.model = formToModel(form);
        this.lastPublished = data.updatedAt;
        // Snapshot what the builder would send, so an unrepresentable property is not read as a change.
        this.savedSnapshot = { name: data.name, form: this.toForm() };
      });
    } catch (err) {
      if (load !== this.latestLoad) return;
      runInAction(() => {
        this.error = err as AxiosError;
        this.isFailure = true;
      });
    } finally {
      if (load === this.latestLoad) {
        runInAction(() => {
          this.isLoaded = true;
        });
      }
    }
  }

  @action.bound
  setName(name: string): void {
    this.name = name;
  }

  findSection(sectionKey: string): AuditSection | undefined {
    return this.model.sections.find((section) => section.key === sectionKey);
  }

  findQuestion(questionKey: string): { section: AuditSection; question: AuditQuestion } | undefined {
    for (const section of this.model.sections) {
      const question = section.questions.find((candidate) => candidate.key === questionKey);
      if (question) return { section, question };
    }
    return undefined;
  }

  @action.bound
  addSection(afterSectionKey?: string): string {
    const key = mintKey();
    const section: AuditSection = {
      key,
      title: this.nextSectionTitle(),
      description: '',
      questions: [],
      actions: [],
    };
    const index = afterSectionKey ? this.model.sections.findIndex((s) => s.key === afterSectionKey) : -1;

    if (index === -1) this.model.sections.push(section);
    else this.model.sections.splice(index + 1, 0, section);

    return key;
  }

  /** Counting sections repeats a name once one has been deleted, so take the lowest free ordinal. */
  private nextSectionTitle(): string {
    const taken = new Set(this.model.sections.map((section) => sectionTitleId(section.title)));
    let ordinal = 1;
    while (taken.has(sectionTitleId(`Section ${ordinal}`))) ordinal += 1;

    return `Section ${ordinal}`;
  }

  @action.bound
  renameSection(sectionKey: string, title: string): void {
    const section = this.findSection(sectionKey);
    if (section) section.title = title;
  }

  @action.bound
  setSectionDescription(sectionKey: string, description: string): void {
    const section = this.findSection(sectionKey);
    if (section) section.description = description;
  }

  @action.bound
  deleteSection(sectionKey: string): void {
    const removed = this.findSection(sectionKey);
    if (!removed) return;

    const removedQuestionKeys = new Set(removed.questions.map((question) => question.key));
    this.model.sections = this.model.sections.filter((section) => section.key !== sectionKey);
    this.pruneActions(removedQuestionKeys, new Set([sectionKey]));
  }

  @action.bound
  reorderSections(orderedKeys: string[]): void {
    const byKey = new Map(this.model.sections.map((section) => [section.key, section]));
    this.model.sections = orderedKeys
      .map((key) => byKey.get(key))
      .filter((section): section is AuditSection => Boolean(section));
    this.pruneBackwardRoutes();
  }

  @action.bound
  moveSection(sectionKey: string, direction: -1 | 1): void {
    const index = this.model.sections.findIndex((section) => section.key === sectionKey);
    const target = index + direction;
    if (index === -1 || target < 0 || target >= this.model.sections.length) return;

    const keys = this.model.sections.map((section) => section.key);
    [keys[index], keys[target]] = [keys[target], keys[index]];
    this.reorderSections(keys);
  }

  sectionNextValue(sectionKey: string): string {
    const section = this.findSection(sectionKey);
    const fallthrough = section?.actions.find((candidateAction) => candidateAction.when === true);

    if (!fallthrough) return SECTION_NEXT_DEFAULT;
    if ('submit' in fallthrough.then) return SECTION_NEXT_SUBMIT;

    return fallthrough.then.nextSection;
  }

  @action.bound
  setSectionNext(sectionKey: string, value: string): void {
    const section = this.findSection(sectionKey);
    if (!section) return;

    const withoutDefault = section.actions.filter((candidateAction) => candidateAction.when !== true);

    if (value === SECTION_NEXT_DEFAULT) {
      section.actions = withoutDefault;
      return;
    }

    section.actions = [
      ...withoutDefault,
      { when: true, then: value === SECTION_NEXT_SUBMIT ? { submit: true } : { nextSection: value } },
    ];
  }

  @action.bound
  addQuestion(
    sectionKey: string,
    questionType: ProtectorType,
    afterQuestionKey?: string,
    lookupType?: string | null
  ): string {
    const section = this.findSection(sectionKey);
    if (!section) return '';

    const isLookup = questionType === ProtectorType.Lookup;
    const question: AuditQuestion = {
      key: mintKey(),
      description: '',
      questionType,
      required: false,
      options: questionType === ProtectorType.YesNoNa ? yesNoNaOptions() : [],
      lookupType: isLookup ? (lookupType ?? null) : null,
      multiple: isLookup,
      image: null,
      unauthoredProperties: {},
    };
    const index = afterQuestionKey ? section.questions.findIndex((q) => q.key === afterQuestionKey) : -1;

    if (index === -1) section.questions.push(question);
    else section.questions.splice(index + 1, 0, question);

    return question.key;
  }

  @action.bound
  relabelQuestion(questionKey: string, description: string): void {
    const found = this.findQuestion(questionKey);
    if (found) found.question.description = description;
  }

  @action.bound
  setRequired(questionKey: string, required: boolean): void {
    const found = this.findQuestion(questionKey);
    if (found) found.question.required = required;
  }

  @action.bound
  deleteQuestion(questionKey: string): void {
    const found = this.findQuestion(questionKey);
    if (!found) return;

    found.section.questions = found.section.questions.filter((question) => question.key !== questionKey);
    this.pruneActions(new Set([questionKey]), new Set());
  }

  @action.bound
  duplicateQuestion(questionKey: string): string {
    const found = this.findQuestion(questionKey);
    if (!found) return '';

    // A duplicate is a new reporting identity, so it mints a key and carries no rules.
    // The image is dropped: sharing one file uuid means removing it from either copy deletes it for both.
    const copy: AuditQuestion = {
      ...cloneDeep(found.question),
      key: mintKey(),
      image: null,
    };
    const index = found.section.questions.findIndex((question) => question.key === questionKey);
    found.section.questions.splice(index + 1, 0, copy);

    return copy.key;
  }

  @action.bound
  reorderQuestions(sectionKey: string, orderedKeys: string[]): void {
    const section = this.findSection(sectionKey);
    if (!section) return;

    const byKey = new Map(section.questions.map((question) => [question.key, question]));
    section.questions = orderedKeys
      .map((key) => byKey.get(key))
      .filter((question): question is AuditQuestion => Boolean(question));
  }

  @action.bound
  moveQuestionToSection(questionKey: string, targetSectionKey: string): void {
    const found = this.findQuestion(questionKey);
    const target = this.findSection(targetSectionKey);
    if (!found || !target || found.section.key === targetSectionKey) return;

    found.section.questions = found.section.questions.filter((question) => question.key !== questionKey);
    target.questions.push(found.question);
    // Actions are section-scoped, so the rules stay behind.
    this.pruneActions(new Set([questionKey]), new Set());
  }

  @action.bound
  setOptions(questionKey: string, options: AuditOption[]): void {
    const found = this.findQuestion(questionKey);
    if (!found) return;

    found.question.options = options;
    const remaining = new Set(options.map((option) => option.key));
    // A rule on a vanished option can never fire, and the server refuses the save.
    found.section.actions = found.section.actions.filter(
      (candidateAction) =>
        candidateAction.when === true ||
        candidateAction.when.equals[0] !== questionKey ||
        candidateAction.when.equals[1].every((answer) => remaining.has(answer))
    );
  }

  @action.bound
  async uploadQuestionImage(questionKey: string, file: File): Promise<void> {
    const found = this.findQuestion(questionKey);
    if (!found) return;

    try {
      const { data } = await this.api.entities.uploadEntityFormImage(this.mode.entityType, file)();
      runInAction(() => {
        // Replacing orphans the old file; delete it once the save lands.
        if (found.question.image) this.orphanedImageUuids.push(found.question.image.uuid);
        found.question.image = { uuid: data.uuid, file: data.file };
      });
    } catch (err) {
      this.store.notification.enqueueErrorSnackbar(apiErrorsWithKeys(err as AxiosError, 'Could not upload image'));
    }
  }

  @action.bound
  removeQuestionImage(questionKey: string): void {
    const found = this.findQuestion(questionKey);
    if (!found?.question.image) return;

    this.orphanedImageUuids.push(found.question.image.uuid);
    found.question.image = null;
  }

  private async deleteOrphanedImages(): Promise<void> {
    const uuids = this.orphanedImageUuids;
    this.orphanedImageUuids = [];

    await Promise.all(
      uuids.map((fileUuid) =>
        this.api.entities
          .deleteEntityFormImage(this.mode.entityType, fileUuid)()
          // Already unreachable — a failed tidy-up should not fail the publish.
          .catch(() => undefined)
      )
    );
  }

  @action.bound
  private pruneActions(removedQuestionKeys: Set<string>, removedSectionKeys: Set<string>): void {
    this.model.sections.forEach((section) => {
      section.actions = section.actions.filter((candidateAction) => {
        if (candidateAction.when !== true && removedQuestionKeys.has(candidateAction.when.equals[0])) return false;
        if ('nextSection' in candidateAction.then && removedSectionKeys.has(candidateAction.then.nextSection)) {
          return false;
        }
        return true;
      });
    });
    this.pruneBackwardRoutes();
  }

  /** A reorder can point a route backwards, which the server refuses for the whole save. */
  @action.bound
  private pruneBackwardRoutes(): void {
    const rank = new Map(this.model.sections.map((section, index) => [section.key, index]));

    this.model.sections.forEach((section, index) => {
      section.actions = section.actions.filter((candidateAction) => {
        if (!('nextSection' in candidateAction.then)) return true;
        const target = rank.get(candidateAction.then.nextSection);
        return target !== undefined && target > index;
      });
    });
  }

  @action.bound
  setActions(sectionKey: string, actions: AuditAction[]): void {
    const section = this.findSection(sectionKey);
    if (section) section.actions = actions;
  }

  @action.bound
  async publish(): Promise<string | undefined> {
    if (this.isPublishing) return undefined;

    runInAction(() => {
      this.isPublishing = true;
      this.saveErrors = [];
    });

    const form = this.toForm();
    // Allow-list: no `settings` (which would grant a public submission link) and no mapping call.
    // Captured untrimmed so the field can be told apart afterwards from one edited mid-flight.
    const nameAtSend = this.name;
    const payload = { name: nameAtSend.trim(), form: form as never };

    try {
      const { data } = this.isNew
        ? await this.api.entities.createEntityForm(this.mode.entityType, payload)()
        : await this.api.entities.updateEntityForm(this.mode.entityType, this.templateId, payload)();

      await this.deleteOrphanedImages();

      runInAction(() => {
        this.templateId = data.uuid;
        this.lastPublished = data.updatedAt;
        // The baseline is what this request sent — a name typed mid-flight is not published.
        this.savedSnapshot = { name: payload.name, form: cloneDeep(form) };
        // Normalise the field to what was sent, so surrounding space does not leave the form dirty
        // forever - but never over a rename typed while the request was in flight.
        if (this.name === nameAtSend) this.name = payload.name;
      });

      return data.uuid;
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>;
      // A field path tells the author nothing; reword it into the item at fault.
      const readable = readableAuditSaveErrors(error, this.model, this.mode.copy.respondents);

      runInAction(() => {
        this.saveErrors = readable;
      });

      this.store.notification.enqueueErrorSnackbar(
        readable[0] || error.response?.data?.message || apiErrorsWithKeys(error, 'Could not publish the template')
      );
      return undefined;
    } finally {
      runInAction(() => {
        this.isPublishing = false;
      });
    }
  }

  /** Derived, not stored: selecting a set copies its options, so the questions stay independent. */
  @computed
  get derivedResponseSets(): ResponseSet[] {
    const seen = new Map<string, ResponseSet>();

    this.model.sections.forEach((section) => {
      section.questions.forEach((question) => {
        if (question.questionType === ProtectorType.YesNoNa) return;
        if (!CHOICE_TYPES.includes(question.questionType)) return;

        if (responseSetLabels(question.options).length < 2) return;

        const id = responseSetId(question.options);
        if (seen.has(id)) return;

        seen.set(id, {
          id,
          label: responseSetLabel(question.options),
          // Pure: minting here would return a new array on every model change and re-render every
          // chooser. `copyResponseSet` mints when a set is actually applied.
          options: question.options,
          questionType: ProtectorType.SingleChoice,
        });
      });
    });

    return [...seen.values()];
  }

  /** A seeded set steps aside for an identical one of the author's, which carries their scores. */
  @computed
  get responseSets(): ResponseSet[] {
    const authored = this.derivedResponseSets;
    const claimed = new Set(authored.map(responseSetIdentity));

    return [...SEEDED_RESPONSE_SETS.filter((set) => !claimed.has(responseSetIdentity(set))), ...authored];
  }

  optionKeysUsedByRules(questionKey: string): string[] {
    const found = this.findQuestion(questionKey);
    if (!found) return [];

    return rulesTriggeredBy(found.section, questionKey).flatMap((rule) => rule.when.equals[1]);
  }

  /**
   * A set is a template, not a shared list. `yes_no_na` keeps its server-fixed keys; anything else
   * gets fresh ones, so the two questions share no option identity.
   */
  copyResponseSet(options: AuditOption[], questionType: ProtectorType): AuditOption[] {
    return options.map((option) => ({
      ...option,
      key: questionType === ProtectorType.YesNoNa ? option.key : mintKey(),
    }));
  }

  isChoiceQuestion(question: AuditQuestion): boolean {
    return CHOICE_TYPES.includes(question.questionType);
  }

  @computed
  get publishedQuestionKeys(): Set<string> {
    const form = this.savedSnapshot?.form;
    if (!form) return new Set();

    return new Set(Object.values(form.properties).flatMap((section) => section.order));
  }

  questionExistsInPublishedForm(questionKey: string): boolean {
    return this.publishedQuestionKeys.has(questionKey);
  }

  /** Both cardinalities store a list, so an unscored flip keeps its key; a scored one mints. */
  @action.bound
  setAllowMultiple(questionKey: string, allow: boolean): void {
    const found = this.findQuestion(questionKey);
    if (!found) return;

    found.question.questionType = allow ? ProtectorType.MultiChoice : ProtectorType.SingleChoice;

    if (allow) {
      // The engine compares against a stored list, so a multi-answer rule matches one exact selection.
      found.section.actions = found.section.actions.filter(
        (candidateAction) => candidateAction.when === true || candidateAction.when.equals[0] !== questionKey
      );
    } else {
      // `scoring_mode` is multiple-choice only; the server refuses it elsewhere.
      delete found.question.unauthoredProperties.scoringMode;
    }
  }

  /** The key is the reporting join key, so a published question mints a new one on a type change. */
  @action.bound
  changeQuestionType(
    questionKey: string,
    questionType: ProtectorType,
    options?: AuditOption[],
    lookupType?: string
  ): string {
    const found = this.findQuestion(questionKey);
    if (!found) return questionKey;

    const { question, section } = found;
    const isLookup = questionType === ProtectorType.Lookup;
    const mintsNewKey = this.questionExistsInPublishedForm(questionKey);
    const nextKey = mintsNewKey ? mintKey() : questionKey;

    const nextOptions = (() => {
      if (questionType === ProtectorType.YesNoNa) return yesNoNaOptions();
      if (options) return options;
      // A single/multi flip keeps its answers; anything else starts empty.
      return CHOICE_TYPES.includes(questionType) && CHOICE_TYPES.includes(question.questionType)
        ? question.options
        : [];
    })();

    const replacement: AuditQuestion = {
      ...question,
      key: nextKey,
      questionType,
      options: nextOptions,
      lookupType: isLookup ? (lookupType ?? question.lookupType) : null,
      multiple: isLookup,
      // `scoring_mode` is multiple-choice only; the server refuses it elsewhere.
      unauthoredProperties:
        questionType === ProtectorType.MultiChoice
          ? question.unauthoredProperties
          : Object.fromEntries(
              Object.entries(question.unauthoredProperties).filter(([name]) => name !== 'scoringMode')
            ),
    };

    section.questions = section.questions.map((candidate) => (candidate.key === questionKey ? replacement : candidate));

    // The old key can no longer be a trigger, and vanished options take their rules with them.
    const survivingOptionKeys = new Set(nextOptions.map((option) => option.key));
    this.model.sections.forEach((candidate) => {
      candidate.actions = candidate.actions
        .filter((candidateAction) => {
          if (candidateAction.when === true) return true;
          const [triggerKey, answers] = candidateAction.when.equals;
          if (triggerKey !== questionKey) return true;
          if (mintsNewKey) return false;
          return answers.every((answer) => survivingOptionKeys.has(answer));
        })
        // Only a single-answer question may branch.
        .filter(
          (candidateAction) =>
            candidateAction.when === true ||
            candidateAction.when.equals[0] !== nextKey ||
            SINGLE_ANSWER_CHOICE_TYPES.includes(questionType)
        );
    });

    return nextKey;
  }
}
